import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, Clock, AlertCircle, CalendarDays } from 'lucide-react';
import { jobApplications } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import ImportantDatesManager from '../UI/ImportantDatesManager';
import { getDateTypeLabel } from '../../utils/dateTypeUtils';
import ConfirmDialog from '../UI/ConfirmDialog';
import { fmtTzDateTimeHuman } from '../../utils/dateFormatUtils';

const ImportantDatesTab = ({ applicationId }) => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    date_type: '',
    date_value: '',
    description: ''
  });

  useEffect(() => {
    fetchDates();
  }, [applicationId]);

  const fetchDates = async () => {
    try {
      setLoading(true);
      const response = await jobApplications.getJobApplicationImportantDates(applicationId);
      
      if (response.success) {
        setDates(response.data);
      } else {
        setError('Failed to fetch dates');
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
      setError('Error loading dates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingDate) {
        // Update existing date
        await jobApplications.updateJobApplicationImportantDate(editingDate.id, formData);
      } else {
        // Create new date
        await jobApplications.addJobApplicationImportantDate(applicationId, formData);
      }
      
      setShowForm(false);
      setEditingDate(null);
      setFormData({ date_type: '', date_value: '', description: '' });
      fetchDates();
    } catch (err) {
      console.error('Error saving date:', err);
      alert('Error saving date');
    }
  };

  const handleEdit = (date) => {
    setEditingDate(date);
    setFormData({
      date_type: date.date_type,
      date_value: date.date_value,
      description: date.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = (date) => {
    setDeleteConfirm(date);
  };

  const confirmDelete = async () => {
    try {
      await jobApplications.deleteJobApplicationImportantDate(deleteConfirm.id);
      fetchDates();
    } catch (err) {
      console.error('Error deleting date:', err);
      alert('Error deleting date');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDate(null);
    setFormData({ date_type: '', date_value: '', description: '' });
  };

  const formatDate = (dateString) => fmtTzDateTimeHuman(dateString);

  const getDateStatusIcon = (date) => {
    if (date.is_overdue) {
      return <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
    } else if (date.is_upcoming) {
      return <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    } else {
      return <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getDateStatusBadge = (date) => {
    if (date.is_overdue) {
      return (
        <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded">
          Overdue
        </span>
      );
    } else if (date.is_upcoming) {
      return (
        <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded">
          Upcoming
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  return (
    <>
    <div className="bg-light-bg-primary p-4 dark:bg-dark-bg-primary sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center space-x-2">
          <CalendarDays className="w-5 h-5" />
          <span>Important Dates</span>
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Date</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Date Form */}
      {showForm && (
        <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg mb-6 border border-light-border-primary dark:border-dark-border-primary">
          <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary mb-4">
            {editingDate ? 'Edit Important Date' : 'Add New Important Date'}
          </h4>
          <ImportantDatesManager
            showTitle={false}
            mode="single"
            allowAddRemove={false}
            importantDates={[{
              id: editingDate?.id || Date.now(),
              date_type: formData.date_type,
              date_value: formData.date_value,
              description: formData.description,
              is_overdue: false,
              is_upcoming: false,
              created_at: editingDate?.created_at || new Date().toISOString()
            }]}
            onImportantDatesChange={(arr) => {
              const d = arr && arr.length > 0 ? arr[0] : { date_type: '', date_value: '', description: '' };
              setFormData({
                date_type: d.date_type || '',
                date_value: d.date_value || '',
                description: d.description || ''
              });
            }}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {editingDate ? 'Update' : 'Add'} Date
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-600 dark:bg-gray-500 hover:bg-gray-700 dark:hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dates List */}
      {dates.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary text-lg mb-2">No important dates found</div>
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary">Add an important date to get started</div>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((date) => (
            <div key={date.id} className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {getDateStatusIcon(date)}
                    <h4 className="text-base font-medium text-light-text-primary dark:text-dark-text-primary">
                      {getDateTypeLabel(date.date_type)}
                    </h4>
                    {getDateStatusBadge(date)}
                  </div>
                  <div className="text-sm md:text-base text-light-text-secondary dark:text-dark-text-secondary mb-1">
                    {formatDate(date.date_value)}
                  </div>
                  {date.description && (
                    <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                      {date.description}
                    </div>
                  )}
                  <div className="text-xs md:text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                    Created: {formatDate(date.created_at)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleEdit(date)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm md:text-base"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(date)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm md:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Date"
      message={deleteConfirm
        ? `Are you sure you want to delete "${getDateTypeLabel(deleteConfirm.date_type)}"? This action cannot be undone.`
        : ''}
      confirmText="Delete"
    />
    </>
  );
};

export default ImportantDatesTab;
